import React, { useState, useRef, useEffect } from 'react';
import cn from 'classnames';
import styles from 'components/common/Form/InputAPI/InputAPI.module.scss';
import { ClearIcon } from 'components/common/icons';
import _ from 'lodash';
import OutsideClickHandler from 'react-outside-click-handler/esm/OutsideClickHandler';
import useAPIOptions from 'hooks/common/useAPIOptions';
import formatLocation from 'helpers/formatLocation';

const InputAPI = ({
    label,
    placeholder,
    wrapperClassName,
    previewClassName,
    valueField,
    onChangeField,
    error,
    onChange: _onChange,
    name,
    labelClassName,
    fillAll,
}) => {
    const defaultValue = valueField ? (valueField.address ? formatLocation(valueField.address) : valueField) : '';
    const [value, setValue] = useState(defaultValue);
    const [isFocused, setFocus] = useState(false);
    const [visibleOptions, setVisibleOptions] = useState(false);
    const inputRef = useRef();
    const [options, setOptions, getOptions] = useAPIOptions(name);

    useEffect(() => {
        setValue(defaultValue);
    }, [defaultValue]);

    const onChange = async (e) => {
        setValue(e.target.value);
        onChangeField({ target: { name: name, value: e.target.value } });
        _onChange(e.target.value);
        if (e.target.value === '') {
            setOptions([]);
            return;
        }
        if (options.length && !visibleOptions) setVisibleOptions(true);
        return await getOptions(e.target.value);
    };

    const onFocus = () => {
        setFocus(true);
        setVisibleOptions(true);
    };

    const onBlur = () => {
        setFocus(false);
    };

    const onClear = (e) => {
        setValue('');
        onChangeField({ target: { name: name, value: null } });
        _onChange('');
        setOptions([]);
        inputRef.current.focus();
    };

    const wrapper = cn(styles.locationInput, wrapperClassName);
    const labelWrapper = cn(styles.label, {
        [styles.primary]: isFocused,
        [styles.filled]: value.length,
        [labelClassName]: labelClassName,
    });
    const clearButtonWrapper = cn(styles.clearButton, { [styles.visible]: value.length });
    const optionsWrapper = cn(styles.options, { [styles.visible]: visibleOptions });
    return (
        <div className={wrapper}>
            {label && <div className={labelWrapper}>{label}</div>}
            <OutsideClickHandler onOutsideClick={() => setVisibleOptions(false)}>
                <input
                    value={value}
                    onChange={onChange}
                    onFocus={onFocus}
                    onBlur={onBlur}
                    placeholder={placeholder}
                    ref={inputRef}
                    className={previewClassName}
                    autoComplete='none'
                />
                <ClearIcon onClick={onClear} className={clearButtonWrapper} />
                <div className={optionsWrapper}>
                    {options.map((option) => (
                        <div
                            className={styles.option}
                            key={option.title}
                            onClick={(e) => {
                                setValue(option.title);
                                onChangeField({ target: { name: name, value: option } });
                                _onChange(option);
                                fillAll({ ...option.address, countryCode: option.address?.countryCode });
                                getOptions(option.title);
                                inputRef.current.focus();
                                setVisibleOptions(false);
                            }}
                        >
                            {option.title}
                        </div>
                    ))}
                </div>
                {error && <div className={cn(styles.error)}>{error}</div>}
            </OutsideClickHandler>
        </div>
    );
};

InputAPI.defaultProps = {
    valueField: '',
    onChangeField: _.noop,
    onChange: _.noop,
    name: 'address',
    fillAll: _.noop,
};

export default React.memo(InputAPI);
